import type { CabinetAppointmentView } from "@planerius/contracts/cabinet"
import { useNavigate } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { useHasPermission } from "../../access/use-access"
import { revealClientPhone } from "../../api/clients.api"
import type { JournalListRow } from "../../journal/journal-list"
import { formatDuration, formatMinute, formatRange } from "../../journal/journal-time"
import { formatMoney } from "../../journal/money"
import { statusVisual } from "../../journal/status-style"
import { cx } from "../../lib/cx"
import { formatDateLong, shiftDate } from "../../lib/format"
import { ProblemScreen } from "../../shell/states"
import { Button } from "../../ui/button"
import { EmptyState } from "../../ui/empty-state"
import { Skeleton } from "../../ui/skeleton"
import styles from "./master-day-screen.module.css"
import { MasterFrame, MasterHead, MasterNoEmployee } from "./master-frame"
import { servicesLabel } from "./master-text"
import type { MasterVisibility } from "./master-visibility"
import { phoneAccessOf, visibleTotalMinor } from "./master-visibility"
import type { MasterSearch } from "./use-master-day"
import { useMasterDay } from "./use-master-day"
import { useVisitActions } from "./use-master-actions"

/**
 * 5-М1 «Мой день» — главный экран мастера.
 *
 * ОН ОТВЕЧАЕТ НА ОДИН ВОПРОС: кто у меня следующий и когда я свободен. Мастер открывает
 * его 5–15 раз за смену по 20–40 секунд, между двумя клиентами, часто мокрыми руками.
 * Если ответ не читается за две секунды, он возвращается к бумажному листку (ТЗ §5.3).
 *
 * ОТСЮДА ТРИ РЕШЕНИЯ, КОТОРЫЕ ЗДЕСЬ ВИДНЫ.
 *
 * 1. ЛЕНТА САМА ПОДКРУЧИВАЕТСЯ К БЛИЖАЙШЕЙ ЗАПИСИ. К четырём часам дня сверху лежат
 *    шесть завершённых визитов, и «следующий» уходит под нижний край. Требование
 *    «ближайшая запись не должна требовать прокрутки» выполняется не вёрсткой, а этим
 *    действием: при открытии дня лента встаёт на ближайшую запись. Один раз — дальше
 *    человек листает сам, и вырывать ленту у него из-под пальца нельзя.
 *
 * 2. «КЛИЕНТ ПРИШЁЛ» — ОДНО КАСАНИЕ С ЛЕНТЫ, без захода в карточку (проверка §5.16).
 *    Кнопка стоит только у ближайшей записи: у всех сразу она превратила бы ленту
 *    в частокол одинаковых кнопок, в котором промахнуться легче, чем попасть.
 *
 * 3. «ЗАПИСАТЬ» НЕ ЗАСЛОНЯЕТ ОТВЕТ. Кнопка прижата к низу отдельной полосой, а лента
 *    прокручивается НАД ней, а не под: перекрывающая кнопка съела бы ровно ту карточку,
 *    ради которой экран открывали.
 */

export function MasterDayScreen(props: { readonly search: MasterSearch }): ReactNode {
  const data = useMasterDay(props.search)
  const canCreate = useHasPermission("appointments.create")
  const canBreak = useHasPermission("busy.manage")
  const navigate = useNavigate()

  const actions = useVisitActions(data.reload)
  const listRef = useRef<HTMLDivElement>(null)
  const scrolledFor = useRef<string | null>(null)

  const day = data.day
  const nextCardId = day?.nextCardId ?? null

  // Подкрутка к ближайшей записи — ровно один раз на день. Ключ по дате, а не по факту
  // «уже крутили»: перелистнув на завтра, человек снова ждёт ответа про ближайшую запись.
  useEffect(() => {
    if (nextCardId === null || scrolledFor.current === data.date) return
    const list = listRef.current
    if (list === null) return
    const card = list.querySelector<HTMLElement>(`[data-card-id="${nextCardId}"]`)
    if (card === null) return
    scrolledFor.current = data.date
    // jsdom не умеет scrollIntoView, и проверки не должны об это спотыкаться.
    if (typeof card.scrollIntoView === "function") {
      card.scrollIntoView({ block: "start" })
    }
  }, [nextCardId, data.date])

  const openVisit = useCallback(
    (appointmentId: string) => {
      void navigate({ to: "/my/visit/$appointmentId", params: { appointmentId }, search: { date: data.date } })
    },
    [navigate, data.date],
  )

  const openBooking = useCallback(
    (startMinute: number | null) => {
      void navigate({
        to: "/my/book",
        // Время передаётся, только когда форму открыли из пустого промежутка ленты:
        // в остальных случаях его выбирает сама форма из свободных окон смены.
        search: { date: data.date, at: startMinute ?? undefined },
      })
    },
    [navigate, data.date],
  )

  const goDay = useCallback(
    (delta: number) => {
      void navigate({ to: "/my/day", search: { date: shiftDate(data.date, delta) } })
    },
    [navigate, data.date],
  )

  const stripe = data.availability.stripe
  const canEdit = data.availability.canEdit

  // Человек не заведён мастером: своего дня у него нет и быть не может. Это состояние
  // доступа, а не пустой день, и предлагать здесь нечего — карточку заводит администратор.
  if (data.employeeId === null) {
    return (
      <MasterFrame active="day">
        <MasterNoEmployee title="Мой день" />
      </MasterFrame>
    )
  }

  if (data.branch === null) {
    return (
      <MasterFrame active="day">
        <MasterHead title="Мой день" />
        <EmptyState
          className={styles.gap}
          title="Вам не открыт ни один филиал"
          text="Доступ к филиалу выдаёт владелец или управляющий салона. Расписание появится сразу после этого."
        />
      </MasterFrame>
    )
  }

  // Сбой ДО первых данных — отдельный экран. Сбой поверх загруженного дня им не является:
  // день остаётся на экране, а сверху появляется полоса с его возрастом (ТЗ §2.11.4).
  if (data.problem !== null && data.result === null) {
    return (
      <MasterFrame active="day">
        <ProblemScreen problem={data.problem} onRetry={data.reload} />
      </MasterFrame>
    )
  }

  const total = data.visibility.money && data.column !== null
    ? visibleTotalMinor(
        data.result?.appointments.filter(
          (item) => item.employeeId === data.employeeId && item.status === "completed",
        ) ?? [],
      )
    : null

  const showBook = canCreate && (day === null || day.kind === "shift" || day.kind === "empty")
  // «Перерыв» — только в день со сменой: вне смены закрывать нечего. Без права busy.manage
  // кнопки нет вовсе (ТЗ §5.7: «салон запретил мастерам ставить перерывы — кнопки нет»).
  const showBreak = canBreak && day !== null && (day.kind === "shift" || day.kind === "empty")

  return (
    <MasterFrame active="day" stripe={stripe} onStripeAction={data.reload}>
      <MasterHead
        title="Мой день"
        subtitle={`${formatDateLong(data.date, data.timeZone)} · ${data.branch.name}`}
        actions={
          <>
            <button
              type="button"
              className={styles.step}
              aria-label="Предыдущий день"
              onClick={() => {
                goDay(-1)
              }}
            >
              <span aria-hidden="true">‹</span>
            </button>
            <button
              type="button"
              className={styles.step}
              aria-label="Следующий день"
              onClick={() => {
                goDay(1)
              }}
            >
              <span aria-hidden="true">›</span>
            </button>
          </>
        }
      />

      <p className={styles.shiftLine}>{day === null ? "Загружаем день…" : day.shiftLine}</p>

      <div className={styles.list} ref={listRef}>
        {day === null ? <DaySkeleton /> : null}

        {day === null || day.empty === null ? null : (
          <EmptyState title={day.empty.title} text={day.empty.text} />
        )}

        {day?.rows.map((row) => (
          <DayRow
            key={rowKey(row)}
            row={row}
            appointments={data.result?.appointments ?? []}
            visibility={data.visibility}
            canEdit={canEdit}
            canCreate={canCreate}
            arriving={actions.running === "arrived"}
            onOpen={openVisit}
            onArrive={actions.arrive}
            onBook={openBooking}
          />
        ))}

        {total === null ? null : (
          <div className={styles.total}>
            <span className={styles.totalLabel}>
              Мои работы за смену · {servicesLabel(day?.doneServices ?? 0)}
            </span>
            <span className={styles.totalValue}>{formatMoney(total)}</span>
          </div>
        )}

        {actions.problem === null ? null : (
          <p className={styles.actionProblem} role="alert">
            {actions.problem.detail ?? actions.problem.title}
          </p>
        )}
      </div>

      {showBook || showBreak ? (
        <div className={cx(styles.footer, styles.footerRow)}>
          {showBreak ? (
            <Button
              className={styles.breakButton}
              deniedReason={canEdit ? undefined : "Без связи перерыв не поставить"}
              onClick={() => {
                void navigate({ to: "/my/break", search: { date: data.date } })
              }}
            >
              Перерыв
            </Button>
          ) : null}
          {showBook ? (
            <Button
              variant="primary"
              block
              className={styles.bookButton}
              deniedReason={canEdit ? undefined : "Без связи записать нельзя"}
              onClick={() => {
                openBooking(null)
              }}
            >
              Записать
            </Button>
          ) : null}
        </div>
      ) : null}
    </MasterFrame>
  )
}

function DaySkeleton(): ReactNode {
  return (
    <div className={styles.skeleton} aria-label="Загружаем расписание">
      {[0, 1, 2, 3].map((index) => (
        <div key={index} className={styles.skeletonCard}>
          <Skeleton width={96} height={13} radius={4} />
          <Skeleton width={150} height={15} radius={4} />
          <Skeleton width={120} height={12} radius={3} />
        </div>
      ))}
    </div>
  )
}

function rowKey(row: JournalListRow): string {
  switch (row.kind) {
    case "appointment":
      return `a:${row.card.id}`
    case "busy":
      return `b:${row.block.id}`
    case "hold":
      return `h:${row.hold.id}`
    case "gap":
      return `g:${String(row.startMinute)}`
  }
}

function DayRow(props: {
  readonly row: JournalListRow
  readonly appointments: readonly CabinetAppointmentView[]
  readonly visibility: MasterVisibility
  readonly canEdit: boolean
  readonly canCreate: boolean
  readonly arriving: boolean
  readonly onOpen: (appointmentId: string) => void
  readonly onArrive: (appointment: CabinetAppointmentView) => void
  readonly onBook: (startMinute: number) => void
}): ReactNode {
  const { row } = props

  if (row.kind === "gap") {
    const minutes = row.endMinute - row.startMinute
    return (
      <div className={styles.gapRow}>
        <span className={styles.gapTime}>{formatRange(row.startMinute, row.endMinute)}</span>
        <span className={styles.gapText}>Свободно {formatDuration(minutes)}</span>
        {/* Ссылка «Записать сюда» исчезает без связи целиком: гасить её было бы обещанием
            действия, которого сейчас нет. Кнопка «Записать» внизу при этом остаётся
            видимой и выключенной — она объясняет, почему записать нельзя. */}
        {props.canCreate && props.canEdit ? (
          <button
            type="button"
            className={styles.gapAction}
            onClick={() => {
              props.onBook(row.startMinute)
            }}
          >
            Записать сюда
          </button>
        ) : null}
      </div>
    )
  }

  if (row.kind === "busy") {
    const minutes = row.block.endMinute - row.block.startMinute
    return (
      <div className={styles.busyRow}>
        <span className={styles.gapTime}>{formatRange(row.block.startMinute, row.block.endMinute)}</span>
        <span className={styles.gapText}>
          {row.block.label} · {formatDuration(minutes)}
        </span>
      </div>
    )
  }

  if (row.kind === "hold") {
    return (
      <div className={styles.busyRow}>
        <span className={styles.gapTime}>{formatMinute(row.hold.startMinute)}</span>
        <span className={styles.gapText}>{row.hold.title}</span>
      </div>
    )
  }

  const { card } = row
  const appointment = props.appointments.find((item) => item.id === card.id) ?? null
  const visual = statusVisual(card.status)
  const past = card.status === "completed" || card.status === "no_show"
  const services =
    card.extraServices > 0 ? `${card.serviceName} + ещё ${String(card.extraServices)}` : card.serviceName
  const price =
    props.visibility.money && appointment?.priceMinor != null ? ` · ${formatMoney(appointment.priceMinor)}` : ""
  const note = appointment?.client.pinnedNotes[0] ?? null

  return (
    <div
      className={cx(styles.card, row.isNext ? styles.cardNext : undefined, past ? styles.cardPast : undefined)}
      data-card-id={card.id}
    >
      <button
        type="button"
        className={styles.cardOpen}
        onClick={() => {
          props.onOpen(card.id)
        }}
      >
        <span className={styles.cardTop}>
          <span className={cx(styles.time, past ? styles.timePast : undefined)}>{card.timeLabel}</span>
          {row.isNext ? <span className={styles.nextChip}>Следующий</span> : null}
          {/* Подпись, а не только цвет: «Завершён», «Ждёт подтверждения», «Не пришёл».
              data-status выбирает пару цветов, но смысл несёт слово. */}
          {visual.label === "" ? null : (
            <span className={styles.statusChip} data-status={card.status}>
              {visual.label}
            </span>
          )}
        </span>
        <span className={styles.client}>
          {card.clientName}
          {card.flags.includes("first") ? <span className={styles.firstVisit}>новый</span> : null}
        </span>
        <span className={styles.service}>
          {services}
          {price}
        </span>
        {note === null ? null : <span className={styles.note}>! {note}</span>}
      </button>

      {row.isNext && appointment !== null ? (
        <div className={styles.cardActions}>
          <Button
            variant="primary"
            size="touch"
            className={styles.arriveButton}
            loading={props.arriving}
            loadingLabel="Отмечаем…"
            deniedReason={props.canEdit ? undefined : "Без связи отметки не сохраняются"}
            onClick={() => {
              props.onArrive(appointment)
            }}
          >
            Клиент пришёл
          </Button>
          {props.visibility.phone && appointment.client.phone !== null ? (
            <a
              className={styles.callButton}
              href={`tel:${appointment.client.phone}`}
              aria-label={`Позвонить: ${appointment.client.displayName}`}
            >
              <span aria-hidden="true">☏</span>
            </a>
          ) : phoneAccessOf(appointment) === "on_open" ? (
            <RevealCallButton appointment={appointment} />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Звонок в одно касание, когда салон включил «мастер видит телефон» (ТЗ 05, §5.x:
 * «звонит клиенту — если салон включил показ телефона»).
 *
 * ПОЧЕМУ КНОПКА, А НЕ ССЫЛКА tel:. Номера в браузере до нажатия нет, и так задумано:
 * лента дня не везёт номера всех клиентов разом (см. phoneAccessOf). Нажатие открывает
 * номер запросом — это и есть одно открытие в журнале салона, который ТЗ требует вести
 * и при включённой настройке (04, Б12), — и сразу его набирает. Для мастера это то же
 * одно касание; для журнала — честная строка «открыл», а не «номер был в браузере».
 */
function RevealCallButton(props: { readonly appointment: CabinetAppointmentView }): ReactNode {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const { appointment } = props

  const call = (): void => {
    if (busy) return
    setBusy(true)
    setFailed(false)
    const send = async (): Promise<void> => {
      try {
        const result = await revealClientPhone({
          clientId: appointment.client.id,
          appointmentId: appointment.id,
        })
        window.location.assign(`tel:${result.phone}`)
      } catch {
        setFailed(true)
      } finally {
        setBusy(false)
      }
    }
    void send()
  }

  return (
    <button
      type="button"
      className={styles.callButton}
      aria-label={`Позвонить: ${appointment.client.displayName}`}
      aria-busy={busy}
      title={failed ? "Номер не открылся: нет связи. Нажмите ещё раз." : undefined}
      onClick={call}
    >
      <span aria-hidden="true">☏</span>
    </button>
  )
}
